import { useEffect, useState } from "react";
import { useLang } from "../../core/lang";
import LangKeys from "../../core/locales/keys";
import { ArrowLeft } from "lucide-react";

function WhatsAppCreditsPage({ onBack }) {
  const { t } = useLang();

  const baseUrl = "https://khata.moxpal.com/api";

  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [autoSend, setAutoSend] = useState(false);

  const [credits, setCredits] = useState(0);
  const [used, setUsed] = useState(0);
  const [premiumLevel, setPremiumLevel] = useState(0);

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    initLoad();
  }, []);

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("login_token") ||
      ""
    );
  };

  const loadData = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${baseUrl}/v2/index.php?path=whatsapp/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success && json.data) {
        const d = json.data;
        const premiumLevelVal = parseInt(d.is_premium || 0);
        setPremiumLevel(premiumLevelVal);
        setCredits(parseInt(d.credits || d.wa_credits || 0));
        setUsed(parseInt(d.used || d.wa_used || 0));
        setAutoSend(d.enabled?.toString() === "1");
      }
    } catch (e) {
      console.log(e);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await fetch(`${baseUrl}/whatsapp/plans.php`);
      const json = await res.json();
      if (json.success) {
        const loadedPlans = Array.isArray(json.plans) ? json.plans : [];
        setPlans(loadedPlans);
        if (loadedPlans.length > 0) {
          const middle = loadedPlans[Math.floor(loadedPlans.length / 2)];
          setSelectedPlan(Number(middle.id));
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const initLoad = async () => {
    setLoading(true);
    await Promise.all([loadData(), loadPlans()]);
    setLoading(false);
  };

  const toggleAutoSend = async (value) => {
    setAutoSend(value);
    try {
      const token = getToken();
      await fetch(`${baseUrl}/v2/index.php?path=whatsapp/toggle`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ enabled: value ? "1" : "0" }),
      });
    } catch (e) {
      console.log(e);
    }
  };

  const startPayment = async () => {
    if (!selectedPlan || paying) return;
    try {
      setPaying(true);
      const token = getToken();
      const selected = plans.find((p) => Number(p.id) === Number(selectedPlan));
      const res = await fetch(`${baseUrl}/v2/index.php?path=razorpay/create-order`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ plan_id: selectedPlan.toString() }),
      });
      const json = await res.json();
      if (!json.success) {
        alert(t(LangKeys.order_failed));
        setPaying(false);
        return;
      }
      const options = {
        key: "rzp_live_STAB6tst63SpHe",
        amount: json.amount,
        currency: "INR",
        name: "Moxpal Khata",
        description: `${selected?.credits} Credits`,
        order_id: json.order_id,
        handler: async function (response) { await verifyPayment(response); },
        modal: { ondismiss: function () { setPaying(false); } },
        theme: { color: "#16A34A" },
      };
      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function () {
        alert(t(LangKeys.payment_failed));
        setPaying(false);
      });
      razorpay.open();
    } catch (e) {
      console.log(e);
      alert(t(LangKeys.payment_error));
      setPaying(false);
    }
  };

  const verifyPayment = async (response) => {
    try {
      const token = getToken();
      const verifyRes = await fetch(`${baseUrl}/v2/index.php?path=razorpay/verify`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          payment_id: response.razorpay_payment_id || "",
          order_id: response.razorpay_order_id || "",
          signature: response.razorpay_signature || "",
          plan_id: selectedPlan.toString(),
        }),
      });
      const json = await verifyRes.json();
      if (json.success) {
        alert(`${t(LangKeys.recharge_successful)} +${json.credits_added}`);
        await loadData();
      } else {
        alert(t(LangKeys.verification_failed));
      }
    } catch (e) {
      console.log(e);
      alert(t(LangKeys.error));
    }
    setPaying(false);
  };

  if (loading) {
    return (
      <div style={onBack ? styles.container : {}}>
        {onBack && (
          <div style={styles.appBar}>
            <button style={styles.backBtn} onClick={onBack}>
              <ArrowLeft size={20} color="#FFFFFF" />
            </button>
            <h3 style={styles.appBarTitle}>{t(LangKeys.whatsapp_balance)}</h3>
          </div>
        )}
        <div style={styles.loaderWrap}>
          <div style={styles.loader}></div>
        </div>
      </div>
    );
  }

  const content = (
    <div style={styles.page}>
      <div style={styles.creditCard}>
        <div style={styles.cardTop}>
          <div>
            <div style={styles.cardLabel}>{t(LangKeys.available_credits)}</div>
            <div style={styles.creditValue}>{credits}</div>
            <div style={styles.balanceText}>{t(LangKeys.whatsapp_balance)}</div>
          </div>
          <div style={styles.switchWrap}>
            <div style={{ ...styles.status, color: autoSend ? "#4ADE80" : "#F87171" }}>
              {autoSend ? t(LangKeys.active) : t(LangKeys.off)}
            </div>
            <button
              style={{
                ...styles.switchBtn,
                justifyContent: autoSend ? "flex-end" : "flex-start",
                backgroundColor: autoSend ? "#2563EB" : "#CBD5E1",
              }}
              onClick={() => toggleAutoSend(!autoSend)}
            >
              <div style={styles.switchDot}></div>
            </button>
          </div>
        </div>
        <div style={styles.bottomStats}>
          <div style={styles.statText}>{t(LangKeys.used)}: {used}</div>
          <div style={styles.statText}>{t(LangKeys.total_purchased)}: {credits + used}</div>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>{t(LangKeys.recharge_plans)}</div>
        {plans.map((plan) => {
          const active = Number(selectedPlan) === Number(plan.id);
          return (
            <div
              key={plan.id}
              style={{ ...styles.planRow, backgroundColor: active ? "#DCFCE7" : "#fff" }}
              onClick={() => setSelectedPlan(Number(plan.id))}
            >
              <div style={styles.left}>
                <div style={{ ...styles.radio, borderColor: active ? "#16A34A" : "#CBD5E1", backgroundColor: active ? "#16A34A" : "#fff" }}>
                  {active && <div style={styles.radioInner}></div>}
                </div>
                <div>
                  <div style={styles.planCredits}>{plan.credits} {t(LangKeys.credits)}</div>
                  {plan.tag && <div style={styles.planTag}>{plan.tag}</div>}
                </div>
              </div>
              <div style={styles.priceWrap}>
                <div style={styles.offerPrice}>₹{plan.offer_price || plan.price}</div>
                {plan.discount_percent && <div style={styles.discount}>{plan.discount_percent}% OFF 🔥</div>}
                {plan.offer_price && <div style={styles.oldPrice}>₹{plan.price}</div>}
              </div>
            </div>
          );
        })}
      </div>

      <button
        style={{ ...styles.payBtn, opacity: selectedPlan && !paying ? 1 : 0.7 }}
        disabled={!selectedPlan || paying}
        onClick={startPayment}
      >
        {paying ? t(LangKeys.processing) : t(LangKeys.proceed_to_pay)}
      </button>
    </div>
  );

  if (onBack) {
    return (
      <div style={styles.container}>
        <div style={styles.appBar}>
          <button style={styles.backBtn} onClick={onBack}>
            <ArrowLeft size={20} color="#FFFFFF" />
          </button>
          <h3 style={styles.appBarTitle}>{t(LangKeys.whatsapp_balance)}</h3>
        </div>
        {content}
      </div>
    );
  }

  return content;
}

const styles = {
  container: { height: '100%', display: 'flex', flexDirection: 'column', background: '#F3F4F6', borderRadius: '24px', overflow: 'hidden' },
  appBar: { height: '64px', background: '#1D4ED8', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '16px', flexShrink: 0 },
  backBtn: { background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  appBarTitle: { color: '#FFFFFF', fontSize: '18px', fontWeight: '700', margin: 0 },
  page: { padding: "16px", flex: 1, overflowY: 'auto' },
  loaderWrap: { height: "60vh", display: "flex", alignItems: "center", justifyContent: "center" },
  loader: { width: "34px", height: "34px", border: "3px solid #DBEAFE", borderTop: "3px solid #2563EB", borderRadius: "50%", animation: "spin 1s linear infinite" },
  creditCard: { background: "linear-gradient(135deg,#0F172A,#1E293B)", borderRadius: "22px", padding: "22px", marginBottom: "18px" },
  cardTop: { display: "flex", justifyContent: "space-between", gap: "16px" },
  cardLabel: { color: "rgba(255,255,255,0.75)", fontSize: "14px" },
  creditValue: { fontSize: "44px", fontWeight: "700", color: "#fff", marginTop: "10px" },
  balanceText: { color: "rgba(255,255,255,0.6)", marginTop: "4px", fontSize: "14px" },
  switchWrap: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "10px" },
  status: { fontWeight: "700", fontSize: "14px" },
  switchBtn: { width: "54px", height: "30px", borderRadius: "999px", border: "none", display: "flex", alignItems: "center", padding: "4px", cursor: "pointer" },
  switchDot: { width: "22px", height: "22px", borderRadius: "50%", backgroundColor: "#fff" },
  bottomStats: { display: "flex", justifyContent: "space-between", marginTop: "18px", gap: "10px", flexWrap: "wrap" },
  statText: { color: "rgba(255,255,255,0.72)", fontSize: "13px" },
  section: { backgroundColor: "#fff", borderRadius: "20px", border: "1px solid #E2E8F0", overflow: "hidden" },
  sectionTitle: { padding: "16px", fontSize: "15px", fontWeight: "600", color: "#111827", borderBottom: "1px solid #F1F5F9" },
  planRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", cursor: "pointer", borderBottom: "1px solid #F1F5F9", gap: "14px" },
  left: { display: "flex", alignItems: "center", gap: "12px" },
  radio: { width: "20px", height: "20px", borderRadius: "50%", border: "2px solid #CBD5E1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  radioInner: { width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#fff" },
  planCredits: { fontSize: "15px", fontWeight: "600", color: "#111827" },
  planTag: { fontSize: "12px", color: "#16A34A", marginTop: "4px" },
  priceWrap: { textAlign: "right" },
  offerPrice: { fontSize: "17px", fontWeight: "700", color: "#111827" },
  discount: { color: "#DC2626", fontSize: "12px", fontWeight: "700", marginTop: "4px" },
  oldPrice: { color: "#9CA3AF", fontSize: "12px", textDecoration: "line-through", marginTop: "4px" },
  payBtn: { width: "100%", height: "52px", borderRadius: "16px", border: "none", backgroundColor: "#16A34A", color: "#fff", fontSize: "15px", fontWeight: "700", marginTop: "20px", cursor: "pointer" },
};

export default WhatsAppCreditsPage;